import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { useFetchArticle } from "components/blog/fetch-hooks";
import { ContentBlock } from "components/blog/content-blocks";
import { ArrowLeft, Calendar } from "lucide-react";
import dayjs from "dayjs";
import { useParams, useHistory } from "react-router-dom";
import { Skeleton } from "components/ui/skeleton";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";

export const ArticleDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const history = useHistory();
  const articleQuery = useFetchArticle(slug);

  const isLoading = articleQuery.isEnabled && articleQuery.isPending;
  const isSuccess = articleQuery.isSuccess;
  const article = articleQuery.data?.data[0];

  if (isLoading) {
    return (
      <div className="max-w-225 mx-auto p-6">
        <Skeleton className="h-100 mb-6 rounded-2xl" />
        <Skeleton className="h-15 mb-4" />
        <Skeleton className="h-50" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="max-w-225 mx-auto p-6">
        <h1 className="text-2xl font-bold">Article not found</h1>
      </div>
    );
  }

  const dateToDisplay = article.displayPublishDate || article.publishedAt;
  const formattedDate = dayjs(dateToDisplay).format("MMMM DD, YYYY");

  return (
    <>
      <ReactQueryErrorWrapper queries={[articleQuery]} mutations={[]} />

      <div className="flex justify-end">
        <Button variant="default" size="sm" onClick={() => history.goBack()}>
          <ArrowLeft className="size-4 mr-1" />
          Back
        </Button>
      </div>

      <div className="max-w-225 mx-auto p-6">
        {isSuccess && (
          <div className="flex flex-col gap-6">
            {/* Cover Image */}
            {article.cover && (
              <img
                src={article.cover.url}
                alt={article.cover.alternativeText || article.title}
                className="w-full h-auto max-h-125 object-cover rounded-2xl"
              />
            )}

            {/* Date and Category */}
            <div className="flex gap-4 items-center flex-wrap">
              <div className="flex gap-2 items-center">
                <Calendar className="size-5" />
                <span className="text-sm text-muted-foreground">
                  {formattedDate}
                </span>
              </div>
              {article.category && (
                <Badge variant="outline">{article.category.name}</Badge>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold">{article.title}</h1>

            {/* Description */}
            <p className="text-md text-muted-foreground">
              {article.description}
            </p>

            {/* Author */}
            {article.author && (
              <div className="flex gap-3 items-center py-4 border-y border-border">
                <Avatar className="size-10">
                  <AvatarImage
                    src={article.author.avatar?.url}
                    alt={article.author.name}
                  />
                  <AvatarFallback>
                    {article.author.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{article.author.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {article.author.title}
                  </p>
                </div>
              </div>
            )}

            {/* Content Blocks */}
            <div className="flex flex-col gap-8">
              {article.blocks.map((block, index) => (
                <ContentBlock key={index} block={block} index={index} />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
